/**
 * boat-detail-adapter — trasforma la barca backend (snake, services/boatService)
 * nel modello @/types Boat (camel) usato da BoatDetailView, BookingWizard e /prenota.
 * Estratto da BoatPageClient per riuso (detail + funnel /prenota = stessa fonte di verità).
 */

import type { Boat } from '@/types';
import type { Boat as BackendBoat } from '@/services/boatService';
import { bookableCapacity } from '@/lib/capacity';
import { getUnitPrice, getPerDayEquivalent, RENTAL_PERIOD_DAYS, type RentalPriceSource } from '@/lib/rental-pricing';

/* eslint-disable @typescript-eslint/no-explicit-any */

export const transformAmenities = (amenities: any[]): any[] => {
  if (!amenities || !Array.isArray(amenities)) return [];
  return amenities.map((a) => ({ id: a.id, key: a.key, label: a.label, icon: a.icon, category: a.category, description: a.description }));
};

export const transformExtras = (extras: any[]): any[] => {
  if (!extras || !Array.isArray(extras)) return [];
  return extras.map((e) => ({
    id: e.id, key: e.key, label: e.label, description: e.description,
    price: e.boat_extras?.custom_price ?? e.BoatExtra?.custom_price ?? e.default_price ?? 0,
    priceType: e.price_type || 'per_day',
    mandatory: e.boat_extras?.mandatory === true || e.BoatExtra?.mandatory === true,
  }));
};

export const transformReviews = (reviews: any[]): any[] => {
  if (!reviews || !Array.isArray(reviews)) return [];
  return reviews.map((r) => ({
    id: r.id, user: r.author_name, rating: r.rating, comment: r.comment,
    date: r.review_date
      ? new Date(r.review_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
      : new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }),
    adminResponse: r.admin_response,
  }));
};

export const transformBackendBoat = (boat: BackendBoat): Boat => {
  const coverIndex = (boat as any).cover_image_index || 0;
  const rawImages = boat.image_urls || [];
  const images = coverIndex > 0 && coverIndex < rawImages.length
    ? [rawImages[coverIndex], ...rawImages.filter((_, i) => i !== coverIndex)]
    : [...rawImages];
  const coverImage = images[0] || '';

  const rangeAvg = (boat as { price_for_range_avg?: number }).price_for_range_avg;
  const usingRange = typeof rangeAvg === 'number' && rangeAvg > 0;
  const rawSeasons = ((boat as { seasonalPrices?: Array<Record<string, unknown>> }).seasonalPrices) || [];
  const todayMid = new Date(); todayMid.setHours(12, 0, 0, 0);
  const curSeason = rawSeasons
    .filter((s) => s.is_active !== false && s.start_date && s.end_date
      && new Date(s.start_date as string) <= todayMid && todayMid <= new Date(s.end_date as string))
    .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0))[0];
  const rentalModeRaw = (boat as { rental_mode?: string }).rental_mode || 'daily';
  const seasonSrc: RentalPriceSource | null = curSeason ? {
    rental_mode: rentalModeRaw,
    price_per_day: curSeason.price_per_day as number | null,
    price_per_week: curSeason.price_per_week as number | null,
    price_per_month: curSeason.price_per_month as number | null,
    price_per_hour: curSeason.price_per_hour as number | null,
  } : null;
  const curSeasonPerDay = seasonSrc ? getPerDayEquivalent(seasonSrc) : null;
  const effectivePrice = usingRange
    ? rangeAvg
    : (curSeasonPerDay != null && curSeasonPerDay > 0
        ? curSeasonPerDay
        : (getPerDayEquivalent(boat as RentalPriceSource) ?? (boat.price_per_day || 0)));
  const periodDays = RENTAL_PERIOD_DAYS[rentalModeRaw] ?? 1;
  const curSeasonUnit = seasonSrc ? getUnitPrice(seasonSrc) : null;
  const unitPrice = usingRange
    ? Math.round((rangeAvg || 0) * periodDays)
    : (curSeasonUnit ?? getUnitPrice(boat as RentalPriceSource) ?? effectivePrice);
  const priceSeasonName = (!usingRange && curSeasonPerDay != null && curSeasonPerDay > 0) ? String(curSeason!.name || '') : undefined;

  return {
    id: boat.id,
    slug: (boat as any).slug,
    name: boat.name,
    type: boat.boat_type || 'other',
    price: effectivePrice,
    unitPrice,
    partialBillingMode: ((boat as { partial_billing_mode?: string }).partial_billing_mode as 'pro_rata' | 'whole_periods' | 'periods_plus_remainder') || 'pro_rata',
    price_per_week: (boat as { price_per_week?: number }).price_per_week ?? undefined,
    price_per_month: (boat as { price_per_month?: number }).price_per_month ?? undefined,
    priceSeasonName,
    priceForRangeAvg: rangeAvg,
    priceForRangeTotal: (boat as { price_for_range_total?: number }).price_for_range_total,
    priceForRangeDays: (boat as { price_for_range_days?: number }).price_for_range_days,
    length: boat.length_meters || 0,
    capacity: bookableCapacity({ max_guests: boat.max_guests, max_capacity: boat.max_capacity }) ?? 6,
    maxCapacity: boat.max_capacity ?? undefined,
    location: boat.location || boat.city || 'Sardegna',
    image: coverImage,
    gallery: images,
    description: boat.description || '',
    amenities: transformAmenities((boat as any).amenities),
    rating: parseFloat((boat as any).rating) || 0,
    reviews: (boat as any).reviews_count || 0,
    reviews_list: transformReviews((boat as any).reviews),
    featured: (boat as any).is_featured === true,
    status: boat.status === 'ACTIVE' ? 'Available' : 'Maintenance',
    licenseRequired: boat.requires_license !== false,
    engine: boat.engine_power_hp ? `${boat.engine_power_hp} CV` : undefined,
    year: boat.year_built,
    port: boat.marina_address || boat.location || undefined,
    port_id: boat.port_id ?? null,
    enginePower: boat.engine_power_hp,
    cabins: boat.cabins || undefined,
    berths: boat.berths || undefined,
    bathrooms: boat.bathrooms || undefined,
    beam: boat.beam_meters ? parseFloat(String(boat.beam_meters)) : undefined,
    draft: boat.draft_meters ? parseFloat(String(boat.draft_meters)) : undefined,
    manufacturer: boat.manufacturer || undefined,
    model: boat.model || undefined,
    fuelNotIncluded: boat.fuel_not_included || false,
    securityDeposit: boat.security_deposit ? parseFloat(String(boat.security_deposit)) : undefined,
    // #11 modalità incasso EFFETTIVA (già risolta boat→vendor in /api/boats/[id]).
    paymentCollectionMode: (boat as any).payment_collection_mode as string | undefined,
    skipperAvailable: boat.skipper_available,
    skipperRequired: boat.skipper_required,
    hostessAvailable: boat.hostess_available,
    coverImageIndex: coverIndex,
    rentalMode: (boat as any).rental_mode || 'daily',
    minRentalDays: (boat as any).min_rental_days,
    maxRentalDays: (boat as any).max_rental_days,
    changeoverDay: (boat as any).changeover_day,
    changeoverCheckinTime: (boat as any).changeover_checkin_time,
    changeoverCheckoutTime: (boat as any).changeover_checkout_time,
    shortStaySurcharge: (boat as any).short_stay_surcharge ? parseFloat(String((boat as any).short_stay_surcharge)) : undefined,
    extras: transformExtras((boat as any).extras),
    price_half_day: boat.price_half_day ? parseFloat(String(boat.price_half_day)) : undefined,
    seasonalPrices: ((boat as any).seasonalPrices || []).map((s: any) => ({
      id: s.id, name: s.name, start_date: s.start_date, end_date: s.end_date,
      price_per_day: parseFloat(String(s.price_per_day)),
      price_half_day: s.price_half_day ? parseFloat(String(s.price_half_day)) : null,
      price_per_week: s.price_per_week != null ? parseFloat(String(s.price_per_week)) : null,
      price_per_month: s.price_per_month != null ? parseFloat(String(s.price_per_month)) : null,
      price_per_hour: s.price_per_hour != null ? parseFloat(String(s.price_per_hour)) : null,
      // Fasce (già markup-ate dall'API boat detail con with_markup): il motore span le
      // legge al booking (client) → mattina≠pomeriggio distinte, parità col server.
      start_md: s.start_md ?? null, end_md: s.end_md ?? null, ranges: s.ranges,
      slots: s.slots,
      priority: s.priority || 0,
      is_active: s.is_active !== false,
    })),
    latitude: boat.latitude ? parseFloat(String(boat.latitude)) : undefined,
    longitude: boat.longitude ? parseFloat(String(boat.longitude)) : undefined,
    cancellationPolicy: (boat as any).cancellation_policies ? {
      id: (boat as any).cancellation_policies.id,
      name: (boat as any).cancellation_policies.name,
      slug: (boat as any).cancellation_policies.slug,
      description: (boat as any).cancellation_policies.description ?? null,
      tiers: Array.isArray((boat as any).cancellation_policies.tiers) ? (boat as any).cancellation_policies.tiers : [],
    } : null,
  };
};
